import { DeviceState } from 'careand-redux/slices/device';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { useAppSelector } from 'careand-redux/store';
import { usePathParams } from 'components/Navigation/NavStack';
import DefaultButton from 'components/Utils/NewDefaultButton';
import React, { useCallback, useEffect } from 'react';
import { Device } from 'services/Native/Device';
import { DevicePermission } from 'services/Native/messages/RequestPermission';
import TopBar from '../TopBar';
import './style.scss';

const buttonProps = {
  allowed: {
    className: 'light',
    disabled: true,
    icon: ['fas', 'circle-check'],
  },
  unknown: {},
  denied: {
    className: 'red solid',
    disabled: true,
  },
};

function getButtonProps(permission: DevicePermission, permissions: DeviceState['permissions']) {
  const value = permissions[permission];
  if (value === true) {
    return buttonProps.allowed;
  }
  if (value === false) {
    return buttonProps.denied;
  }
  return buttonProps.unknown;
}

export default () => {
  const nav = useNavStack('current');
  const permissions = useAppSelector((state) => state.device.permissions);
  const isTelemedicine = useAppSelector((state) => state.booking.bookedAppointmentId);
  const areAllPermissionsGiven = [
    permissions.mic,
    isTelemedicine ? true : permissions.push,
    permissions.video,
  ].reduce((acc, it) => acc && typeof it === 'boolean', true);

  const onClickToAdvance = useCallback(() => {
    nav.popTo('/', { animation: 'push' });
  }, []);

  const onClick = (permission: DevicePermission) => {
    nav.push(`/permissions/${permission}`);
  };

  return (
    <div id="permissions-page" className="page">
      <TopBar title="Permissions" />
      <div className="content">
        <span className="prompt">
          You need to allow our application to make audio and video calls for telemedicine
          {!isTelemedicine
            ? ', and send you push notifications regarding lab results and updates to your Health Records.'
            : '.'}
        </span>
        <div className="buttons">
          <DefaultButton
            name="Allow Camera"
            {...getButtonProps('video', permissions)}
            onClick={() => onClick('video')}
          />
          <DefaultButton
            name="Allow Microphone"
            {...getButtonProps('mic', permissions)}
            onClick={() => onClick('mic')}
          />
          {!isTelemedicine && (
            <DefaultButton
              name="Allow Push Notifications"
              {...getButtonProps('push', permissions)}
              onClick={() => onClick('push')}
            />
          )}
          {areAllPermissionsGiven ? (
            <DefaultButton name="Continue" className="continue-button" onClick={onClickToAdvance} />
          ) : (
            <span className="link" onClick={onClickToAdvance}>
              Remind Me Later
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const PermissionDetailData = {
  push: {
    title: 'Enable Notifications',
    imageSrc: '/img/permissions/ios/push.webp',
    prompt:
      'Care& uses notifications to remind you of: Appointments, updates to your health records and chat messages.',
  },
  video: {
    title: 'Enable Camera',
    imageSrc: '/img/permissions/ios/video.webp',
    prompt:
      'Care& uses secure video call technology for all of our video calls. For us to contact you, we need access to your camera.',
  },
  mic: {
    title: 'Enable Microphone',
    imageSrc: '/img/permissions/ios/mic.webp',
    prompt:
      'Care& uses secure video call technology for all of our video calls. To be able to talk to your practitioner during a audio or video call, we need access to your microphone.',
  },
};

export const PermissionDetail = () => {
  const { permission } = usePathParams() as { permission: 'push' | 'video' | 'mic' };
  const permissions = useAppSelector((state) => state.device.permissions);
  const nav = useNavStack('current');
  const { prompt, imageSrc, title } = PermissionDetailData[permission];

  useEffect(() => {
    if (typeof permissions[permission] === 'boolean') {
      nav.pop();
    }
  }, [permissions]);

  const onClickGotIt = () => {
    Device.requestPermission(permission);
  };
  const onClickLater = () => {
    nav.pop();
  };

  return (
    <div id="permission-detail-page" className={`page is-${permission}`}>
      <TopBar title={title} />
      <div className="content">
        <div className="center-content">
          <span>{prompt}</span>
          <img className="example" src={imageSrc} alt="alert example" />
          <img className="pointer" src="/img/icons/icon-arrow-round-up.svg" />
          <span>
            Click <b>{permission === 'push' ? 'Allow' : 'OK'}</b> when the Popup appears
          </span>
        </div>
        <div className="buttons">
          <DefaultButton name="Got it!" onClick={onClickGotIt} />
          <DefaultButton name="Maybe Later" className="light" onClick={onClickLater} />
        </div>
      </div>
    </div>
  );
};

export function shouldShowPermissionScreen(permissions: DeviceState['permissions']) {
  const allPermissionsProvided = [permissions.mic, permissions.push, permissions.video].reduce(
    (acc, it) => acc && typeof it === 'boolean',
    true,
  );
  return Device.isMobile() && !allPermissionsProvided;
}

export function shouldShowTelemedecinePermissionsScreen(permissions: DeviceState['permissions']) {
  const allPermissionsProvided = [permissions.mic, permissions.video].reduce(
    (acc, it) => acc && typeof it === 'boolean',
    true,
  );
  const isMobileDevice = Device.isMobile();
  return Device.isMobile() && !allPermissionsProvided;
}
